/**
 * UI-layer host entry (CAOS-130).
 *
 * Import order matters for "no flash of a default brand": the compiled design-system floor and
 * the host chrome are render-blocking `<link>`s Vite hoists into <head>, so the full `--caos-*`
 * substrate is in place before the body paints; the kernel-resolved token seam is then stamped
 * synchronously on the root below, still before first paint.
 */
import '../../design-system/dist/caos-default.css'; // the --caos-* floor + self-hosted CSP-safe fonts
import '@caos/ui'; // registers every <caos-*> custom element as a side effect (aliased to caos-ui/src)
import './host.css';
import './hidden.css'; // CAOS-615 — the hidden attribute means hidden on every host surface

import { SurfaceHost } from './host.js';
import { initTheme } from './theme.js';
import { loadPersonPreferences } from './person-preferences.js';
import { declarePagePending } from './page-pending.js';
import { signInSurface } from './surfaces/sign-in.js';
import { emptyWorkspaceSurface, configureEmptyWorkspace } from './surfaces/empty-workspace.js';
import { errorSurface, configureErrorSurface } from './surfaces/error.js';
import {
  shellFrameSurface,
  configureShellFrame,
  applyShellFrameRoute,
  openAssistant,
  edgeTabChoices,
  MY_FEEDBACK_PATH,
} from './surfaces/shell-frame.js';
import {
  demoDecision,
  demoShellRuntime,
  demoInstallClient,
  demoRebootDecision,
  demoShellRuntimeAfterInstall,
  demoInstalledPackageKeys,
} from './demo/decision-source.js';
import { openExchange } from './install/exchange-surface.js';
import { setInstallClient } from './install/install-client.js';
import { PROVING_CATALOG } from './install/proving-catalog.js';
import { fetchKernelDecision } from './kernel-decision-source.js';
import { fetchOrgMetadata, fetchSessionIdentity } from './metadata-source.js';
import {
  enrichShellPlan,
  buildShellRuntime,
  narrowPlanToPreview,
  narrowPlanToHeldPermissions,
  environmentLabelFor,
  appDeclaresItsLanding,
  viewerInputContext,
} from './shell-wiring.js';
import { fetchPreviewState, setPreviewRole, activePreviewFrom, NO_PREVIEW } from './preview-source.js';
import { installSessionSignOutSync } from './session-sync.js';
import { renderPage } from './content-source.js';
import { resolveShellContent, resolveEditPageHref, fallbackNode, objectHomeMap } from './content-source.js';
import { recordHomesOf } from './record-address.js';
import { renderProfileSurface } from './surfaces/profile.js';
import { renderSetupSurface } from './surfaces/setup-surface.js';
// CAOS-1583 — the device approval screen. Host chrome, not a baked surface and not metadata:
// approving a device is a platform capability that must work in an org that has installed nothing,
// and the two baked surfaces are baked because they precede installation, which this does not.
import {
  DEVICE_ACCESS_TOKEN_HOURS,
  isDeviceApprovalPath,
  renderDeviceApprovalSurface,
} from './surfaces/device-approval.js';
import { answerDeviceGrant, fetchPendingGrant } from './device-auth-client.js';
import { renderAppHomeSurface } from './surfaces/app-home-surface.js';
import { renderMyProfileSurface, isMyProfilePath, MY_PROFILE_PATH } from './surfaces/my-profile-surface.js';
import { contributedPreferences } from './surfaces/contributed-preferences.js';

import {
  createShellRefresher,
  installShellRefreshTriggers,
  type ShellRefresher,
} from './shell-refresh.js';
import type { OrgMetadata } from './metadata-source.js';

/** CAOS-137 — the reserved shell route for the Profile surface (reached from the account menu). */
const PROFILE_PATH = '/profile';

/** CAOS-140 — the Setup admin app owns the `/setup` route and everything beneath it. */
function isSetupPath(pathname: string): boolean {
  return pathname === '/setup' || pathname.startsWith('/setup/');
}

/**
 * CAOS-694/CAOS-342 — the reserved `/<appKey>/settings` route the account menu's "Settings" entry
 * opens. Not a member of `app.tabs` (it is reached only from the account menu), so it is matched
 * here rather than falling through the ordinary tab-route resolver. Returns the app key when the
 * path is exactly this shape (`/<app>/settings`, no further segments), `undefined` otherwise.
 */
function appSettingsPathAppKey(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  return segments.length === 2 && segments[1] === 'settings' ? segments[0] : undefined;
}

/**
 * CAOS-325 — the Atlantis AI app opens on its own overview surface. Like Setup, it is a destination the
 * engine renders rather than a list of records, so it owns its route: `/ai` is the dashboard.
 */
const AI_APP_PATH = '/ai';

import type { SurfaceDecision } from './kernel-contract.js';

/**
 * CAOS-607 — what the banner says, read from the session itself.
 *
 * Returns the two names the banner names, or undefined for an ordinary session. `targetLabel` is
 * whoever this session resolves AS — the person already on screen everywhere else — and
 * `realLabel` is the human actually behind it.
 *
 * FAILS QUIET, and that direction is deliberate. A network error leaves the banner off, which is
 * exactly what an ordinary session shows; the alternative failure — announcing an impersonation
 * that is not happening — would be a false statement about who somebody is. The kernel is still
 * the thing enforcing every limit, so a missing banner never means missing enforcement.
 */
async function currentImpersonation(
  actingAsLabel: string | undefined,
): Promise<{ targetLabel: string; realLabel?: string; expiresAt?: string } | undefined> {
  try {
    const res = await fetch('/api/impersonation/current', { credentials: 'same-origin' });
    const payload = (await res.json()) as
      | {
          ok?: boolean;
          data?: { impersonating?: boolean; impersonator?: string; expiresAt?: string | null };
        }
      | null;
    if (payload?.ok !== true || payload.data?.impersonating !== true) return undefined;
    const impersonator = payload.data.impersonator;
    // The deadline is the kernel's, not this page's: it is read from the session row on every load,
    // so a tab left open overnight comes back with the real remaining time rather than one counted
    // down from whenever the tab happened to start.
    const expiresAt = payload.data.expiresAt;
    return {
      targetLabel: actingAsLabel ?? 'another person',
      ...(typeof impersonator === 'string' && impersonator.length > 0 ? { realLabel: impersonator } : {}),
      ...(typeof expiresAt === 'string' && expiresAt.length > 0 ? { expiresAt } : {}),
    };
  } catch {
    return undefined;
  }
}


/**
 * CAOS-607 — who this person may act as, asked once per load.
 *
 * The kernel decides eligibility and this returns nothing but its answer. It is asked here, beside
 * `currentImpersonation`, because the list is a fact about the session: the shell re-assembles its
 * regions on every in-app navigation, so resolving it there would be one request per click for a
 * list that changes about never.
 *
 * FAILS QUIET, and towards absence. A refusal is the ORDINARY response — almost nobody signed in
 * holds this permission — so an error here is not a fault to report; it simply means the account
 * menu offers no such entry, which is what it has always shown.
 */
async function impersonationTargets(): Promise<{ id: string; label: string }[]> {
  try {
    const res = await fetch('/api/impersonation/candidates', { credentials: 'same-origin' });
    const payload = (await res.json()) as { ok?: boolean; data?: { people?: unknown } } | null;
    if (payload?.ok !== true) return [];
    const people = Array.isArray(payload.data?.people) ? payload.data.people : [];
    return people
      .filter((p): p is { id: string; label: string } =>
        typeof p === 'object' && p !== null &&
        typeof (p as Record<string, unknown>)['id'] === 'string' &&
        typeof (p as Record<string, unknown>)['label'] === 'string')
      .map((p) => ({ id: p.id, label: p.label }));
  } catch {
    return [];
  }
}


// CAOS-294 — activate the CAOS brand palette + apply the persisted light/dark preference BEFORE first
// paint, so every surface (sign-in, empty-workspace, shell) honours the saved theme with no flash.
initTheme();

// CAOS-507 — the session cookie is shared by same-origin tabs already; follow the explicit sign-out
// signal so this document leaves the now-revoked workspace immediately rather than discovering it
// only after its next request fails.
installSessionSignOutSync(() => window.location.assign('/'));

const mount = document.getElementById('caos-host-root');
if (!(mount instanceof HTMLElement)) {
  throw new Error('UI host mount #caos-host-root is missing from the document.');
}

const host = new SurfaceHost(mount)
  .register(signInSurface)
  .register(emptyWorkspaceSurface)
  .register(errorSurface)
  .register(shellFrameSurface);

// Expose the re-render hook FIRST so a driver (Playwright / the kernel API caller) can feed a
// SurfaceDecision at runtime and prove a live token swap without a reload — available even while
// the initial decision is still in flight below.
declare global {
  interface Window {
    __caosHost?: { render(decision: SurfaceDecision): void };
  }
}
window.__caosHost = host;

/**
 * In PRODUCTION the decision comes from the kernel's boot endpoint (a thin same-origin fetch —
 * `kernel-decision-source.ts`). The `?surface=` fixture stays available for local verification
 * behind a dev flag (Vite `import.meta.env.DEV`), so a developer can drive any surface / brand
 * without a running kernel, while a real build always asks the kernel.
 */
const url = new URL(window.location.href);
const useFixture =
  import.meta.env.DEV && (url.searchParams.has('surface') || url.searchParams.has('brand'));


// CAOS-243/267: the shell frame needs session/route context not carried on the kernel plan (the
// signed-in user, active app, active tab). PRODUCTION derives this from the session + route +
// installed metadata (see `renderProductionDecision` below); only the dev FIXTURE supplies the
// representative demo context here, so `?surface=hand-to-shell` renders a populated frame.
// The dev fixture is navigable too: wire its navigate seam to the same client router production
// uses, so `?surface=hand-to-shell` exercises the persistent-shell app/tab switching (CAOS-300).
// CAOS-287 — the fixture gets the affordance WIRED, not merely drawn. The launcher renders the
// same "Get more apps" row here as in production, so leaving `getMoreApps` off would put a control
// on the dev screen that does nothing when pressed — the precise defect CAOS-299 was raised for, and
// the fixture is where a person looks first. It reboots through the SAME fixture path the
// empty-workspace CTA uses, so both install routes in the fixture agree about what happens next.
// No install client is passed: `setInstallClient(demoInstallClient)` below already makes the
// in-memory boundary the default one the picker picks up.
//
// CAOS-290 — the fixture opens the EXCHANGE, the same surface production opens, and is deliberately
// given NO exchange client. There is no kernel behind `?surface=`, so the registry read fails and
// the surface renders its degraded shape: the platform apps listed and installable, and a sentence
// saying why the published half is empty. That is the state a real org without `setup.access` or on
// an older platform sees, so the fixture exercises it rather than papering over it.
//
// A fixture that stubbed the registry would ALSO be a bundle hazard, which is the measured reason
// rather than the tidy one: `demo/decision-source.ts` is imported unconditionally, so anything it
// reaches is in the production bundle whatever `import.meta.env.DEV` says — a first version of
// CAOS-1728 put `pvk_` keys into shipped JavaScript exactly that way. The configuration that drives
// this surface against real published packages is `?proving` against a live org (CAOS-1505), not a
// fixture.
const fixtureGetMoreApps = (): void => {
  openExchange({
    installedPackageKeys: demoInstalledPackageKeys(),
    onInstalled: () => {
      const next = demoRebootDecision(url);
      if (next.surface === 'hand-to-shell') {
        configureShellFrame({ ...demoShellRuntimeAfterInstall(url), getMoreApps: fixtureGetMoreApps });
      }
      host.render(next);
    },
  });
};
if (useFixture) {
  configureShellFrame({
    ...demoShellRuntime(url),
    getMoreApps: fixtureGetMoreApps,
    navigate: (href) => clientNavigate(href),
  });
}

// CAOS-211: wire the empty-workspace CTA behaviour (install → reboot → re-render).
//   • PRODUCTION reboot re-runs the boot decision live (re-fetch `boot/surface` + re-render), so a
//     now-present shell master flips empty-workspace → the painted shell with no full reload. The
//     install itself goes through the default HTTP install client (`/api/packages/install`).
//   • FIXTURE mode swaps in the in-memory install client and a reboot that reads the fixture's
//     installed-set, so the whole flow is drivable against `vite dev` with no Engine.
if (useFixture) {
  setInstallClient(demoInstallClient);
  // CAOS-662 — trying again from the error screen re-asks the same source the screen came from.
  configureErrorSurface({ retry: () => host.render(demoDecision(url)) });
  configureEmptyWorkspace({
    reboot: () => {
      const next = demoRebootDecision(url);
      if (next.surface === 'hand-to-shell')
        configureShellFrame({ ...demoShellRuntimeAfterInstall(url), getMoreApps: fixtureGetMoreApps });
      host.render(next);
    },
  });
} else {
  // CAOS-662 — trying again from the error screen re-runs boot live, the same as a reboot.
  configureErrorSurface({
    retry: async () => {
      await renderProductionDecision(await fetchKernelDecision());
    },
  });
  configureEmptyWorkspace({
    reboot: async () => {
      await renderProductionDecision(await fetchKernelDecision());
    },
  });
}

/**
 * CAOS-1728 — the proving catalog, applied over whichever branch above ran.
 *
 * A SEPARATE STATEMENT, AND THE SHAPE IS LOAD-BEARING. `configureEmptyWorkspace` merges, so this
 * overrides only `catalog` and leaves each branch's `reboot` intact — which is what lets the
 * proving loop be driven both ways: `?surface=empty-workspace&proving` against the in-memory
 * fixture, and `?proving` alone against a real org through `httpInstallClient`, which is the
 * configuration that installs a published package by universal key for real.
 *
 * It is written as `if (import.meta.env.DEV && …)` rather than as a flag consulted inside the
 * calls above because THAT SHAPE DID NOT TREE-SHAKE, and the difference was measured rather than
 * assumed. The first version computed `const useProvingCatalog = import.meta.env.DEV && …` and
 * spread `...(useProvingCatalog ? { catalog: PROVING_CATALOG } : {})` into both branches; a grep of
 * the built bundle then found `pvk_` three times and all three fixture package keys in the shipped
 * JavaScript. Rollup folds `import.meta.env.DEV` to `false`, but it would not eliminate the
 * reference through the `const` and the conditional spread, so `proving-catalog.ts` was retained
 * whole. With the test directly in the `if`, the block is dead code, the last reference to
 * `PROVING_CATALOG` goes with it, and the module shakes out.
 *
 * So the guarantee that the fixtures cannot reach a customer is a property of THIS statement, and
 * it is checked on the artifact — see the bundle grep recorded on the pull request, which asserts
 * zero occurrences against controls that must be present.
 */
if (import.meta.env.DEV && url.searchParams.has('proving')) {
  configureEmptyWorkspace({ catalog: PROVING_CATALOG });
}

/**
 * CAOS-1517 — the shell is otherwise resolved exactly once per document load, so a package
 * uninstalled anywhere (the CLI, another tab, another admin) kept rendering its pinned surfaces in
 * this tab until a manual reload. The refresher re-asks the kernel at the moments below and
 * re-renders ONLY when the composition actually changed — see `shell-refresh.ts` for why an
 * unconditional re-render, and an `error` decision on a refresh, would each be worse than the bug.
 */
const shellRefresher: ShellRefresher = createShellRefresher({
  fetchDecision: () => fetchKernelDecision(),
  // The other half of the composition. The kernel plan does not carry the installed apps at all —
  // `enrichShellPlan` builds the `navigation-entries` seam from org metadata — so without this an
  // app package's removal would leave the raw decision identical and its tile would stay mounted.
  // `fetchOrgMetadata` is fail-soft and answers with EMPTY metadata when it cannot reach the
  // kernel, which is indistinguishable from "every app was uninstalled"; an org rendering a shell
  // always has at least its own components, so no labels AND no apps means the fetch failed. Say
  // "undetermined" rather than "none", or a dropped request empties the nav strip.
  fetchAppKeys: async () => {
    const meta = await fetchOrgMetadata();
    if (meta.apps.length === 0 && meta.labels.size === 0) return null;
    return meta.apps.map((app) => app.key);
  },
  render: (decision) => renderProductionDecision(decision),
});
installShellRefreshTriggers({ window, document }, shellRefresher);

/**
 * CAOS-267 — render a real kernel decision in PRODUCTION. For `hand-to-shell` it joins the boot
 * plan with the org's installed metadata (retrieve): the apps + tabs become the App Launcher and the
 * app-scoped nav, the injection labels resolve to real names, and the active app/tab comes from the
 * browser route — so a signed-in user sees the actually-installed workspace, navigable. Every other
 * surface (sign-in, empty-workspace, error) renders straight through, unchanged.
 */
async function renderProductionDecision(decision: SurfaceDecision): Promise<void> {
  if (decision.surface !== 'hand-to-shell' || !decision.shell) {
    // CAOS-1517 — seeded for EVERY surface, not just `hand-to-shell`: an org whose shell master is
    // uninstalled falls back to empty-workspace, and that transition has to be detectable in both
    // directions. No apps are rendered outside the shell, so the app half is empty here and the
    // signature ignores it.
    shellRefresher.seed(decision, []);
    host.render(decision);
    return;
  }
  // CAOS-470 — the signed-in person's preferences (theme, navigation arrangements, rail pinning) load
  // beside the metadata, so the first paint of the workspace already shows them.
  // CAOS-606 — the preview reads alongside them, not after: it decides which apps and tabs the
  // first paint draws, so a serial read would paint the unpreviewed workspace and then take it
  // away, which is the flicker a person previewing is least able to interpret.
  const [meta, identity, , previewRead] = await Promise.all([
    fetchOrgMetadata(),
    fetchSessionIdentity(),
    loadPersonPreferences(),
    fetchPreviewState(),
  ]);
  // CAOS-606 — `null` means the preview read could not be made, which is NOT "nothing is being
  // previewed": the kernel narrows from the session row either way. So the ROLE IN FORCE is taken
  // from whichever of the two reads answered, preferring the preview read's own answer and falling
  // back to what whoami reported, while the NARROWING is only ever applied from a read that
  // actually succeeded.
  //
  // The two failure directions are not symmetric, and this picks the survivable one. A banner with
  // no narrowing shows a person more tabs than the previewed role has, and every one of them opens
  // onto data the kernel has already narrowed — confusing, and explained by the banner right above
  // it. Narrowing with no banner shows them a smaller workspace with nothing to say why, which is
  // indistinguishable from having lost access, and is a wrong answer to the very question they
  // opened the preview to ask.
  const preview = previewRead ?? NO_PREVIEW;
  const activePreview = activePreviewFrom(previewRead, identity.preview);
  // CAOS-1517 — record the composition now on screen, so the next refresh compares against what the
  // user is looking at rather than against the first decision of the session. Seeded from `meta`
  // rather than from the plan, because the apps are not in the plan.
  shellRefresher.seed(decision, meta.apps.map((app) => app.key));
  // CAOS-606 — the enriched plan, then narrowed to what the previewed role can reach. With no
  // preview in force `narrowPlanToPreview` returns the plan it was given, so this line is inert for
  // everybody who is not previewing. The narrowing happens HERE, before the runtime and the content
  // resolver are built from it, so the launcher, the nav strip and the routes all agree about which
  // apps exist — a plan narrowed for the chrome alone would leave a tab unreachable in the bar and
  // still openable by address.
  // CAOS-157 — and narrowed again to what this person may actually use. The assistant anchor is
  // the one edge contribution gated by a system permission (`ai.use`), so a person without it is
  // not shown a panel that would refuse every message. Applied AFTER the preview narrowing and on
  // the real holding rather than the previewed role; `narrowPlanToHeldPermissions` carries the
  // reasoning for both, including why an ABSENT `heldPermissions` keeps the tab and an empty one
  // removes it.
  const shell = narrowPlanToHeldPermissions(
    narrowPlanToPreview(enrichShellPlan(decision.shell, meta), preview.visibleObjects),
    identity,
  );
  // CAOS-342 — the account block names the person, the role they are viewing as, and the workspace.
  // The identity read is authoritative; the sign-in hint is only a fallback for when it cannot answer.
  let userName: string | undefined = identity.identifier;
  if (!userName) {
    try {
      userName = window.sessionStorage.getItem('caos_user_hint') ?? undefined;
    } catch {
      /* sessionStorage may be unavailable; the account block degrades gracefully. */
    }
  }
  const userSub = [identity.roles[0]?.label, identity.orgLabel].filter(Boolean).join(' · ') || undefined;
  // CAOS-1809 — the same two facts, spelled the way a PLACED COMPONENT asks for them, so a piece
  // declaring `source: "userContext"` is answered from the one session read above rather than from a
  // second one. Built here and threaded down, not resolved per surface: a surface that re-derived it
  // could disagree with the account block drawn from `runtime.user` three lines of wiring away.
  //
  // Absent when nobody is signed in, which leaves those inputs OFF the element — the state
  // `caos-topbar` documents as a bar above a sign-in screen.
  const viewer = viewerInputContext(userName ? { name: userName, ...(userSub ? { sub: userSub } : {}) } : undefined);
  // CAOS-607 — is this session acting as somebody else? Asked of the SERVER on every load rather
  // than remembered in the client, because the banner has to be right on a page somebody bookmarked,
  // reloaded or opened in a second tab — and because the session can lapse past its cap at any
  // moment, after which there is nothing to announce. A failure here leaves the banner off, which is
  // the same thing an ordinary session shows and never a false claim that somebody is impersonating.
  const impersonation = await currentImpersonation(identity.identifier);
  // CAOS-607 — and, when this is NOT already an impersonated session, who this person may act as.
  // Not asked from inside an impersonation: starting a second one from within the first is refused
  // by the kernel, so offering the entry there would only produce a control that cannot work.
  const impersonationOptions = impersonation ? [] : await impersonationTargets();
  // CAOS-300 — persistent client-rendered shell. An in-app move within the SAME origin pushes a
  // history entry and repaints ONLY the app-scoped regions in place (top chrome stays mounted).
  // Anything the live frame can't route in place (cross-origin, sign-out, an unmounted frame) falls
  // back to a real document load, so nothing dead-ends.
  const navigate = (href: string): void => clientNavigate(href);
  // CAOS-306 — the content resolver joins the route with the org's real data (Data API) + object
  // catalog to render the list view / record page. It seeds the initial content region AND drives the
  // in-place repaint on every persistent navigation, so a client-routed move shows real content (a
  // list's seeded rows, a record's fields) without a full document load. Undefined ⇒ the app landing.
  /**
   * CAOS-1583 — resolve the device approval screen for this request.
   *
   * The code comes from the link the person followed (`?code=`) or from the form on the screen
   * when they opened the plain verification URL instead. Both paths land here; neither is special.
   *
   * An UNREACHABLE server renders the code form again rather than a problem message. That is a
   * deliberate choice between two wrong-feeling options: telling somebody their code was not found
   * when the server was down would send them off re-typing a code that was always correct, and
   * there is no fourth problem state to render without inventing one. Asking again is the answer
   * that costs a person the least when we do not know.
   */
  /**
   * The approval screen re-renders itself in place: entering a code, approving and denying all
   * change what the page should say without changing the route. Tracked as one node and swapped with
   * `replaceWith` rather than routed, because a history entry per keystroke of a consent flow would
   * put the dead states back in the person's Back button.
   */
  let mountedDeviceApproval: Node | null = null;
  const replaceDeviceApproval = (el: Node): void => {
    const current = mountedDeviceApproval;
    if (current && current.parentNode) (current as ChildNode).replaceWith(el);
    mountedDeviceApproval = el;
  };
  const renderDeviceApproval = async (search: string): Promise<Node> => {
    const fromLink = new URLSearchParams(search).get('code') ?? '';
    const show = async (code: string): Promise<Node> => {
      if (!code.trim()) {
        return renderDeviceApprovalSurface({
          userCode: '',
          credentialHours: DEVICE_ACCESS_TOKEN_HOURS,
          onSubmitCode: (typed) => void show(typed).then((el) => replaceDeviceApproval(el)),
        });
      }
      const found = await fetchPendingGrant(code);
      if (!found.ok) {
        if ('problem' in found) {
          return renderDeviceApprovalSurface({
            userCode: code,
            problem: found.problem,
            credentialHours: DEVICE_ACCESS_TOKEN_HOURS,
          });
        }
        return renderDeviceApprovalSurface({
          userCode: '',
          credentialHours: DEVICE_ACCESS_TOKEN_HOURS,
          onSubmitCode: (typed) => void show(typed).then((el) => replaceDeviceApproval(el)),
        });
      }
      const answer = async (approve: boolean): Promise<void> => {
        const result = await answerDeviceGrant(code, approve);
        // Re-resolve either way. On success the grant is no longer pending, so the screen renders
        // "already answered" — which is the true state and the right thing for the person to see,
        // because the next thing they do is go back to the app that asked.
        void result;
        replaceDeviceApproval(await show(code));
      };
      return renderDeviceApprovalSurface({
        userCode: code,
        grant: found.value,
        credentialHours: DEVICE_ACCESS_TOKEN_HOURS,
        onApprove: () => void answer(true),
        onDeny: () => void answer(false),
      });
    };
    const el = await show(fromLink);
    mountedDeviceApproval = el;
    return el;
  };
  const resolveContent = (route: string): Promise<Node | undefined> => {
    // CAOS-345 — a route carries its query (`?view=…` names the saved list view to open on), so split
    // it: destinations are matched on the PATH, and the query travels on to the list surface.
    const [pathname = '', search = ''] = splitRoute(route);
    // CAOS-372 — everything about YOU is one app now: your account, your preferences, and what you
    // reported. The two reserved routes those used to live at still resolve, into the section of the
    // app that owns them, so every link anyone has kept lands where it always did.
    if (isMyProfilePath(pathname) || pathname === PROFILE_PATH) {
      // CAOS-749 — the packages' contributed settings, derived from the shell frame plan on each
      // resolve. Deriving here rather than once at boot keeps this route the only thing that knows
      // Preferences has a seam at all; the plan itself is the one the boot resolved.
      //
      // CAOS-751 — and the switch per edge-anchored source, resolved from the SAME plan the gutter
      // is built from, so the list can never name a tab the workspace does not have. Both come off
      // that one plan, and they answer different questions: a contributed setting is one a package
      // declared for itself, an edge tab switch is the workspace's own answer about a seam it
      // renders, offered for every anchored source whether or not its package declared anything.
      return Promise.resolve(
        renderMyProfileSurface({
          pathname,
          navigate,
          userName,
          // CAOS-1809 — and the keyed form, for a piece placed on the Sign-in Activity page.
          ...(viewer ? { viewer } : {}),
          meta,
          contributedPreferences: contributedPreferences(shell),
          edgeTabs: edgeTabChoices(shell),
        }),
      );
    }
    if (pathname === MY_FEEDBACK_PATH) {
      // Neither is passed here: this route lands on the Feedback section, and Preferences — the only
      // thing that draws either — is not on screen.
      return Promise.resolve(
        renderMyProfileSurface({
          pathname: `${MY_PROFILE_PATH}/feedback`,
          navigate,
          userName,
          ...(viewer ? { viewer } : {}),
          meta,
        }),
      );
    }
    // CAOS-140/141 — the Setup admin app owns `/setup*`: its console rail + Object Manager + the
    // coming-soon sections, all painted from the org's metadata catalog.
    // CAOS-1583 — the device approval screen. Placed before the metadata-driven routes because it
    // must resolve in every org regardless of what is installed: a headless client prints this URL
    // and a person types it, so an org whose packages do not happen to carry the screen would leave
    // that person at a dead link with no way to sign in at all.
    if (isDeviceApprovalPath(pathname)) return renderDeviceApproval(search);
    if (isSetupPath(pathname))
      return Promise.resolve(
        renderSetupSurface({ meta, pathname, navigate, ...(viewer ? { viewer } : {}) }),
      );
    // CAOS-325 gave the AI app an overview drawn here because the app declared no tabs at all, so
    // there was nothing for the ordinary route to resolve. CAOS-439 gives it a landing of its own,
    // and this branch now YIELDS to one rather than being deleted outright.
    //
    // Why yield rather than delete. The host and the app's package ship separately, so an org that
    // has this build and not that package still has an app with no tabs — and deleting this branch
    // would land those people on nothing at all. Keeping it as a fallback means a declared landing
    // wins the moment the package arrives, and nobody regresses in the meantime. The branch can go
    // when no org lags, which is a decision about what is deployed rather than about this code.
    //
    // It is also worth naming what the fallback IS, because the ticket exists to stop copying it:
    // an overview measured over the whole visible set, phrased in the reference's sales terms. It
    // presumes a sales application is installed, which is exactly why it is no longer the answer
    // for an app that can now say what it opens on.
    if (pathname === AI_APP_PATH || pathname === `${AI_APP_PATH}/`) {
      if (!appDeclaresItsLanding(meta, AI_APP_PATH)) {
        return Promise.resolve(
          renderAppHomeSurface({
            meta,
            navigate,
            onAsk: () => openAssistant(),
            ...(identity.roles[0]?.label ? { userRole: identity.roles[0]!.label } : {}),
          }),
        );
      }
    }
    // CAOS-694/CAOS-342 — the account menu's per-app "Settings" entry opens `/<app>/settings`.
    // Re-checked here (not just in the menu), the same defence-in-depth `isSetupPath` etc. get:
    // a person who never saw the control cannot reach the surface by typing the address either.
    const settingsAppKey = appSettingsPathAppKey(pathname);
    if (settingsAppKey) {
      return Promise.resolve(
        renderAppSettingsSurface({
          meta,
          appKey: settingsAppKey,
          identity,
          pathname,
          ...(viewer ? { viewer } : {}),
        }),
      );
    }
    return resolveShellContent({
      plan: shell,
      meta,
      pathname,
      navigate,
      search,
      // CAOS-1624 — from the whoami the boot already made, so a list view's viewer-scoped filter
      // can be decided without a second read. Absent when that read failed, which keeps the
      // narrowing on.
      //
      // DELIBERATELY NOT FOLLOWING THE PREVIEWED ROLE, unlike `administers` above, and the
      // asymmetry is the point rather than an oversight. `administers` gates a menu entry, so
      // showing an administrator's Settings link under a "Previewing as Sales Rep" banner teaches
      // the wrong thing and following the preview is right. This decides whether a NARROWING
      // CONDITION is added to a read the Engine has already answered — and CAOS-606 narrows the
      // object and field planes only, resolving the RECORD plane from the real principal. So a
      // preview that also scoped this would add `owner_id = $currentUser` and show the
      // administrator THEIR OWN rows while calling it the rep's view: neither person's list, and a
      // fiction the banner above it would appear to vouch for. Left unscoped, the preview shows
      // what the Engine is really returning to this caller, which is the honest half.
      ...(identity.heldPermissions ? { heldPermissions: identity.heldPermissions } : {}),
      // CAOS-1809 — who is reading, for a placed piece declaring `source: "userContext"`. Travels
      // beside `heldPermissions` because both are facts about this session and neither is a fact
      // about the route; the content resolver forwards it to every page it draws.
      ...(viewer ? { viewer } : {}),
      // CAOS-438 — an app that declares an `appLanding` page opens on what needs attention IN THAT
      // APP, derived from the objects it puts on its own tabs. The app key comes from the address
      // the tab was reached at; nothing the page authored reaches this call.
      appLanding: (appKey) =>
        renderAppHomeSurface({
          meta,
          navigate,
          appKey,
          onAsk: () => openAssistant(),
          ...(identity.roles[0]?.label ? { userRole: identity.roles[0]!.label } : {}),
        }),
    });
  };
  const environmentLabel = environmentLabelFor(identity);
  const runtime = buildShellRuntime(shell, {
    pathname: window.location.pathname,
    userName,
    ...(userSub ? { userSub } : {}),
    ...(identity.roles.length > 0 ? { userRoles: identity.roles } : {}),
    // CAOS-606 - while previewing, the per-app Settings entry follows the previewed role too. A
    // menu that says "Previewing as Sales Rep" three lines above a Settings entry only an
    // administrator has is the sort of half-applied preview that teaches somebody the wrong thing.
    administers: preview.administers ?? identity.administers,
    ...(preview.roles.length > 0 ? { previewRoles: preview.roles } : {}),
    ...(activePreview ? { activePreview } : {}),
    ...(environmentLabel ? { environmentLabel } : {}),
    ...(impersonation ? { impersonation } : {}),
    ...(impersonationOptions.length > 0 ? { impersonationOptions } : {}),
    // CAOS-608 — "Edit this page" is offered only where it leads somewhere, and ASKED PER ROUTE
    // rather than resolved once: the frame keeps its top chrome across an in-place navigation, so a
    // value fixed at boot would outlive the page it was true for. Resolved from the same route and
    // the same projection the content region draws from, so the entry cannot disagree with the
    // record actually on screen. The query and fragment are stripped because the gate reads path
    // segments.
    editPageHrefFor: (route) =>
      resolveEditPageHref({ plan: shell, meta, pathname: route.split('?')[0]!.split('#')[0]! }),
    navigate,
  });
  // CAOS-290 — the App Launcher's "Get more apps" affordance opens the in-shell EXCHANGE, over the
  // SAME install seam the empty-workspace picker drives (`install-client.ts`, CAOS-211). One write
  // path, two front doors: nothing here talks to `/api/packages/install` itself, so a change to how
  // installs are made cannot leave this surface behind still doing it the old way.
  //
  // This replaces CAOS-287's use of `openPackagePicker` here. The picker is untouched and is still
  // what the EMPTY-WORKSPACE CTAs open — it lists the compiled-in manifest, which is the right and
  // only thing to offer an org that has nothing yet. The exchange additionally reads the
  // cross-publisher registry (`GET packages/published`, CAOS-1682), so an org that has a workspace
  // sees what everyone has published and not only what shipped with the platform.
  //
  // Seeded with the org's installed packages so an app already in the workspace reads as INSTALLED
  // rather than being offered again. On success the refresher re-runs the boot decision and
  // re-renders, which is what puts the new app in the switcher without a full page load — and it is
  // the refresher rather than a bespoke reload so that an install and a background change to the
  // installed set converge on the same repaint.
  const getMoreApps = (): void => {
    openExchange({
      installedPackageKeys: meta.installedPackageKeys,
      onInstalled: async () => {
        // The refresher's OUTCOME is deliberately dropped: the install already succeeded and the
        // surface has said so, and a repaint that found nothing to change is not a failure of it.
        await shellRefresher.refresh();
      },
    });
  };
  const content = await resolveContent(window.location.pathname + window.location.search);
  configureShellFrame({
    ...runtime,
    getMoreApps,
    resolveContent,
    ...(content ? { content } : {}),
    // CAOS-606 - a full reload rather than a repaint. Everything on screen was resolved under
    // the role that was in force when it was fetched: the metadata, the narrowed plan, the
    // layout of the record open in the content region. Re-running boot is what makes the whole
    // workspace agree about which role it is showing, and agreeing is the entire product here.
    setPreviewRole: (roleKey: string | null) => {
      void setPreviewRole(roleKey).then((ok) => {
        if (ok) window.location.reload();
      });
    },
  });
  host.render({ surface: 'hand-to-shell', shell });
}

/**
 * CAOS-694/CAOS-342 — render the app's `settingsPage` at `/<app>/settings`, using the SAME generic
 * page renderer a tab-bound page already uses (`renderPageSurface`, CAOS-388/CAOS-503) — no new
 * rendering primitive, just a new destination for the one that exists. Every way this can come up
 * short says so honestly rather than rendering a blank or silently falling through to the app's
 * ordinary tab landing:
 *   - the app is unknown, or declares no `settingsPage` at all — never reached from the menu (it
 *     never offers the entry), but a typed/stale address must still say something, not blank;
 *   - the person does not administer this app — same "not offered, and not reachable either" floor
 *     the account menu itself enforces, checked again here (defence in depth, `main.ts`'s own
 *     `resolveContent` already does this for `/setup*` and `/my_profile*`);
 *   - the app names a settingsPage this workspace's install does not actually have — a dangling
 *     reference the deploy gate (`validateAppSettingsPages`) should never let through, but the read
 *     path reports it plainly rather than assuming metadata can never drift.
 */
function renderAppSettingsSurface(input: {
  readonly meta: OrgMetadata;
  readonly appKey: string;
  readonly identity: { readonly administers: readonly string[] };
  readonly pathname: string;
  /** CAOS-1809 — who is reading, for a piece this settings page places with `source: "userContext"`. */
  readonly viewer?: Readonly<Record<string, string>>;
}): HTMLElement {
  const { meta, appKey, identity, pathname, viewer } = input;
  const app = meta.apps.find((a) => a.key === appKey);
  if (!app) {
    return fallbackNode(
      'There’s no app here',
      `“${appKey}” isn’t an app in this workspace. It may have been renamed or uninstalled.`,
    );
  }
  if (!identity.administers.includes(appKey)) {
    return fallbackNode(
      'You don’t administer this app',
      `Settings for “${app.label}” are only open to people who administer it. Ask an administrator for access.`,
    );
  }
  if (!app.settingsPage) {
    return fallbackNode(
      `“${app.label}” has no settings yet`,
      'This app has not declared a settings surface — there is nothing to configure here.',
    );
  }
  const page = meta.pages.get(app.settingsPage);
  if (!page) {
    return fallbackNode(
      `“${app.label}” settings has nothing behind it`,
      `This app points its settings at a page called “${app.settingsPage}”, and this workspace does not have it. Install the package that provides it, or point the app somewhere else.`,
    );
  }
  // CAOS-1616 — as the tab-bound page path does, so a settings page's roster can open a record
  // too. Derived from installed metadata here because this entry point is not handed the shell
  // plan; the homes it reads are the same authored ones, from `meta.apps`.
  return renderPage(page, {
    meta,
    appLabel: app.label,
    pathname,
    objectHomes: objectHomeMap(
      app.tabs,
      appKey,
      recordHomesOf(meta.apps.map((a) => ({ sourceKey: a.key, tabs: a.tabs }))),
    ),
    ...(viewer ? { viewer } : {}),
  });
}

/** Split a route into its path and its query string (`['/a/b', '?view=x']`). */
function splitRoute(route: string): [string, string] {
  const q = route.indexOf('?');
  if (q < 0) return [route.split('#')[0]!, ''];
  return [route.slice(0, q), route.slice(q).split('#')[0]!];
}

/**
 * CAOS-300 — route an in-app navigation without a full document load. Same-origin paths push a
 * history entry and ask the live shell frame to repaint in place; if the frame can't (no frame
 * mounted) or the target is cross-origin, fall back to a full load so the move still completes.
 */
function clientNavigate(href: string): void {
  let target: URL;
  try {
    target = new URL(href, window.location.origin);
  } catch {
    window.location.assign(href);
    return;
  }
  if (target.origin !== window.location.origin) {
    window.location.assign(href);
    return;
  }
  const path = target.pathname + target.search + target.hash;
  if (path === window.location.pathname + window.location.search + window.location.hash) return;
  if (applyShellFrameRoute(target.pathname + target.search)) {
    window.history.pushState({}, '', path);
    // CAOS-1517 — a pushState navigation fires no event, so the `popstate` trigger cannot see it.
    // Fire-and-forget: the repaint above already happened and must not wait on the kernel.
    void shellRefresher.refresh();
  } else {
    window.location.assign(href); // no live frame to repaint — let the browser load it
  }
}

// Back/forward repaint the live frame in place (no document load), preserving the persistent shell.
window.addEventListener('popstate', () => {
  applyShellFrameRoute(window.location.pathname + window.location.search);
});

void (async () => {
  if (useFixture) {
    host.render(demoDecision(url));
    return;
  }
  // CAOS-610 — the boot is the host's own public route: a URL arrived at from a link, a call in
  // flight, no control that started it and no layout to preserve. Say the page is pending, and
  // retract that BEFORE painting — the paint clears the mount, and a retraction that runs after it
  // is talking to a node nobody is listening to. `resolved` is idempotent, so the `finally` covers
  // a throw on the way without double-clearing on the way that worked.
  const resolved = declarePagePending(mount);
  try {
    const decision = await fetchKernelDecision();
    resolved();
    await renderProductionDecision(decision);
  } finally {
    resolved();
  }
})();
